// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.D3TooltipDrawing.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { d3 } from '../Izenda.D3Wrapper';
import { isNil } from 'lodash/fp';
import { TOOLTIP_VERTICAL_SPACING, TOOLTIP_HORIZONTAL_SPACING } from 'logic/data/Izenda.D3VizEngineConstants';

/**
 * Add tooltip events to the points and labels of the chart
 * @param {d3.Selection} tooltip The tooltip element selection
 * @param {HTMLDivElement} container The chart container
 * @param {Object} options The chart options
 * @param {d3.Selection} contentNodes The points to add tooltip
 * @param {d3.Selection} labelNodes The labels to add tooltip
 * @param {Object} margin The object contains chart margin
 * @param {Number} additionalTopTranslate The additional top translate when having legend
 * @param {Number} additionalLeftTranslate The additional left translate when having legend
 */
export function addTooltipContent(
  tooltip: d3.Selection,
  container: HTMLDivElement,
  tooltipFormatter: Function,
  contentNodes: d3.Selection,
  labelNodes: d3.Selection,
  margin: Object,
  additionalTopTranslate: Number,
  additionalLeftTranslate: Number
) {
  // The current point of tooltip
  let currentPoint = null;

  /**
   * Reposition the tooltip
   * @param {Boolean} isTooltipAppear Is this tooltip appear in this function
   * @param {d3.Selection} tooltipSelection The tooltip selection
   */
  function repositionTooltip(isTooltipAppear: Boolean, tooltipSelection: d3.Selection) {
    const tooltipNode = tooltipSelection.node();
    const pointBox = this.getBBox();
    const { width: tooltipWidth, height: tooltipHeight } = tooltipNode.getBoundingClientRect();
    const maxWidth = container.clientWidth - (margin.left + margin.right);
    const maxHeight = container.clientHeight - (margin.top + margin.bottom);

    // Initial position without any spacing yet
    const initialLeft = pointBox.x + (pointBox.width - tooltipWidth) / 2 + margin.left;
    const initialTop = pointBox.y + (pointBox.height - tooltipHeight) / 2 + margin.top;

    // Center horizontal position
    const horizontalCenterPosition = margin.left + pointBox.x + pointBox.width / 2;

    let left = initialLeft + additionalLeftTranslate;
    let top = initialTop + additionalTopTranslate;

    // Add spacing to avoid overlap the data region
    if (top - TOOLTIP_VERTICAL_SPACING >= margin.top) {
      top -= TOOLTIP_VERTICAL_SPACING;
    } else if (top + top - TOOLTIP_VERTICAL_SPACING <= maxWidth - margin.bottom) {
      top += TOOLTIP_VERTICAL_SPACING;
    }

    // Reposition tooltip inside the container
    if (top + tooltipHeight > maxHeight - margin.bottom) {
      top -= top + tooltipHeight - maxHeight + margin.bottom;
    }
    if (left + tooltipWidth > maxWidth - margin.right) {
      left -= left + tooltipWidth - maxWidth + margin.right;
    }

    // If the container so small in height that we cannot spacing vertical
    // We will spacing horizontal instead
    if (top <= margin.top) {
      // We will priority spacing right first
      if (horizontalCenterPosition + TOOLTIP_HORIZONTAL_SPACING + tooltipWidth <= maxWidth) {
        left = horizontalCenterPosition + TOOLTIP_HORIZONTAL_SPACING;
      } else {
        left = horizontalCenterPosition - TOOLTIP_HORIZONTAL_SPACING - tooltipWidth;
      }
    }

    // Make sure the tooltip is inside the container
    left = Math.max(margin.left, left);
    top = Math.max(margin.top, top);

    // Do not use transition when the first tooltip is show

    tooltipSelection
      .classed('animate', !isTooltipAppear)
      .style('top', `${top}px`)
      .style('left', `${left}px`)
      .style('max-width', `${maxWidth}px`);
  }

  // On enter a new tooltip
  function mouseover(data) {
    const { point, serie } = data;
    // Do not use transition when the first tooltip is show
    const isTooltipAppear = isNil(currentPoint);
    currentPoint = this;
    tooltip
      .html(tooltipFormatter(point, serie))
      .call(repositionTooltip.bind(this, isTooltipAppear))
      .style('opacity', 1);
  }

  // On leave a data point
  function mouseleavePoint() {
    if (this !== currentPoint) {
      tooltip
        .classed('animate', true)
        .style('opacity', 0)
        .style('top', 0)
        .style('left', 0);
    }
  }

  // Hide tooltip
  function hideTooltip() {
    currentPoint = null;
    tooltip.classed('animate', true).style('opacity', 0);
  }

  // On leave series group
  function mouseleaveSeriesGroup() {
    const [leftMousePosition, topMousePosition] = d3.mouse(this);
    const { width, height } = this.getBBox();

    // Avoid the case when mouse over the tooltip and the event register as leave chart
    if (leftMousePosition > width || leftMousePosition < 0 || topMousePosition > height || topMousePosition < 0) {
      hideTooltip();
    }
  }

  d3.select(container)
    .select('.d3-series-group')
    .on('mouseleave.seriesGroup', mouseleaveSeriesGroup);

  d3.select(container).on('mouseleave.container', hideTooltip);

  contentNodes.on('mouseover.tooltip', mouseover).on('mouseleave.tooltip', mouseleavePoint);
  labelNodes.on('mouseover.tooltip', mouseover).on('mouseleave.tooltip', mouseleavePoint);
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/d3/commonDrawing/Izenda.D3TooltipDrawing.js